import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import 'swiper/css/bundle';

import { useQuestionnaire } from '../../hooks/use-questionnaire';
import QuestionnaireQuestion from './question';
import CommonAnimatedLoader from '../common/animated/loader';
import QuestionnaireProgressBar from './progress-bar';
import { QuestionnaireType } from '../../entities/questionnaire-type';
import { parameters, strings } from '../../localization/strings';
import AlertPrimary from '../common/alerts/alert-primary';
import ContainerMd from '../common/containers/containerMd';
import { useSite } from '../../hooks/use-site';
import { useAuth } from '../../hooks/use-auth';
import { getLocalizedPath } from '../../helpers/localization-helper';
import { pushGTMEvent, GTM_EVENTS } from '../../utils/gtm-events';
import { getProductName, getProductSlug } from '../../utils/product-names';
import { Question } from '../../entities/question';

interface ComponentProps {
  productId: number;
}

function QuestionnaireSliderComponent({ productId }: ComponentProps) {
  const { isSelfCo, locale, region } = useSite();
  const { user } = useAuth();
  const {
    type,
    questions,
    initialSlide,
    isLast,
    answerHash,
    orderHash,
    isQuotaReached,
    patientHashContext,
    extraProductId,
  } = useQuestionnaire();
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [current, setCurrent] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const hasTrackedStart = useRef(false);

  const { patientHash } = useParams();

  // Helper to track individual question views
  const trackQuestionView = useCallback(
    (question: Question | undefined, questionIndex: number, totalQuestions: number) => {
      if (!productId || !question) return;

      pushGTMEvent(GTM_EVENTS.QUESTIONNAIRE_STEP_VIEWED, {
        product_id: productId,
        product_slug: getProductSlug(productId),
        product_name: getProductName(productId),
        question_step: questionIndex + 1,
        question_id: question.id,
        question_key: question.key || 'unknown',
        question_type: question.type,
        question_title: question.title?.substring(0, 100) || '',
        total_steps: totalQuestions,
        progress_percent: Math.round(((questionIndex + 1) / totalQuestions) * 100),
      });
    },
    [productId],
  );

  if (productId && productId === parameters().product.freeScreenerAsitId) {
    document.location.href = parameters().freeResearchUrl;
  }

  if (productId && productId === parameters().product.researchId) {
    document.location.href = parameters().freeResearchUrl;
  }

  if (isLast) {
    if (orderHash) {
      navigate(`/research/success/${orderHash}/${productId}`);
    } else if (productId && productId === parameters().product.freeScreenerAsitId) {
      document.location.href = parameters().freeResearchUrl;
    } else if (productId && productId === parameters().product.researchId) {
      document.location.href = parameters().freeResearchUrl;
      // navigate(`/questionnaire/register`);
    } else if (extraProductId) {
      navigate(`/questionnaire/select-presale/${extraProductId}`);
    } else if (type === QuestionnaireType.Onboarding) {
      // navigate(`/questionnaire/answer/${answerHash}`);
      if (user) {
        if (isSelfCo) {
          navigate(getLocalizedPath(parameters().shipment, locale, region));
        } else {
          navigate(getLocalizedPath(parameters().selectPatient, locale, region));
        }
      } else {
        navigate(getLocalizedPath(parameters().register, locale, region));
      }
    } else if (type === QuestionnaireType.Feedback) {
      navigate(`/questionnaire/success`);
    } else if (type === QuestionnaireType.WeeklyTreatment) {
      if (patientHashContext && answerHash) {
        navigate(`/questionnaire/treatment-weekly/success/${patientHashContext}/${answerHash}`);
      }
    } else if (type === QuestionnaireType.Anamnesis) {
      navigate(`/questionnaire/anamnesis/${patientHash}/send`);
    } else {
      // navigate(`/payments/checkout/${answerHash}`);
      navigate(getLocalizedPath(parameters().register, locale, region));
    }
  }

  useEffect(() => {
    if (swiper && initialSlide) {
      swiper.slideTo(initialSlide);
    }
  }, [swiper, initialSlide]);

  useEffect(() => {
    // Only track once per component mount
    if (hasTrackedStart.current) return;

    const visibleQuestions = questions.filter((q) => q.visible);

    // Wait for data to be fully loaded
    if (visibleQuestions.length === 0 || !productId || current !== 0) {
      return;
    }

    hasTrackedStart.current = true;

    // Only fire add_to_cart for pre-purchase flow (URL without '/repeat')
    const isPostPurchase = location.pathname.includes('/repeat');

    if (!isPostPurchase) {
      pushGTMEvent(GTM_EVENTS.ADD_TO_CART, {
        product_id: productId,
        product_slug: getProductSlug(productId),
        product_name: getProductName(productId),
      });
    }

    // Track questionnaire started
    pushGTMEvent(GTM_EVENTS.QUESTIONNAIRE_STARTED, {
      product_id: productId,
      product_slug: getProductSlug(productId),
      product_name: getProductName(productId),
      total_questions: visibleQuestions.length,
      questionnaire_type: type,
    });

    // Track initial question view (will be at initialSlide position)
    const initialQuestion = visibleQuestions[initialSlide];

    trackQuestionView(initialQuestion, initialSlide, visibleQuestions.length);
  }, [current, initialSlide, location.pathname, productId, questions, trackQuestionView, type]);

  const initializeSwiper = (s: SwiperClass) => {
    setSwiper(s);
  };

  if (isQuotaReached) {
    return (
      <ContainerMd>
        <AlertPrimary
          type="failed"
          title={type === QuestionnaireType.Feedback ? strings().error.feedback.title : strings().error.common.title}
          text={
            type === QuestionnaireType.Feedback
              ? strings().error.feedback.description
              : strings().error.common.description
          }
        />
      </ContainerMd>
    );
  }

  return questions.length ? (
    <>
      <QuestionnaireProgressBar total={questions.filter((q) => q.visible).length} current={current} />
      <Swiper
        slidesPerView={1}
        noSwiping
        onSwiper={initializeSwiper}
        onSlideChange={(s) => {
          const currentIndex = s.activeIndex;

          setCurrent(currentIndex);

          const visibleQuestions = questions.filter((q) => q.visible);
          const currentQuestion = visibleQuestions[currentIndex];

          trackQuestionView(currentQuestion, currentIndex, visibleQuestions.length);
        }}
        initialSlide={initialSlide}
        allowTouchMove={false}
      >
        {questions
          .filter((q) => q.visible)
          .map((question) => {
            return (
              <SwiperSlide key={question.id}>
                <QuestionnaireQuestion productId={productId} question={question} />
              </SwiperSlide>
            );
          })}
      </Swiper>
    </>
  ) : (
    <CommonAnimatedLoader />
  );
}

export default QuestionnaireSliderComponent;
